import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { VENDOR_VIEW_SOURCES, normalizeVendorCategory } from 'lib/analytics';
import { useAnalytics } from './useAnalytics';

const KNOWN_SOURCES = Object.values(VENDOR_VIEW_SOURCES);

/**
 * Fire a single vendor_profile_viewed event when a vendor profile page has
 * loaded. Used by both the claimed profile page (VendorDetailsPage) and the
 * crawled/unclaimed one (CrawledVendorDetailsPage).
 *
 * Exactly-once semantics:
 *   - Nothing fires until dedupeKey is truthy (i.e. the vendor data loaded).
 *   - A given dedupeKey fires at most once per mount; re-renders and
 *     authUser changes (which change the track() identity) cannot re-fire.
 *   - Navigating profile A -> B changes dedupeKey, so B fires as a fresh view.
 *
 * source comes from router Link state (location.state.source) set by the
 * originating list surface. Anything not in VENDOR_VIEW_SOURCES — including a
 * missing state on refresh/external entry — is reported as 'direct'.
 *
 * @param {string|number|null|undefined} dedupeKey stable id for this profile
 * @param {object} props event properties (vendor_id, vendor_name, vendor_category, ...)
 */
const useVendorProfileViewTracker = (dedupeKey, props = {}) => {
    const { track, events } = useAnalytics();
    const location = useLocation();

    // Keep the effect dependent only on dedupeKey — everything else flows
    // through refs so changing prop/callback identity can't re-fire the event.
    const propsRef = useRef(props);
    propsRef.current = props;
    const trackRef = useRef(track);
    trackRef.current = track;
    const sourceRef = useRef(location.state?.source);
    sourceRef.current = location.state?.source;

    const firedForKeyRef = useRef(null);

    useEffect(() => {
        if (dedupeKey === null || dedupeKey === undefined || dedupeKey === '') return;
        if (firedForKeyRef.current === dedupeKey) return;
        firedForKeyRef.current = dedupeKey;

        const rawSource = sourceRef.current;
        const source = KNOWN_SOURCES.includes(rawSource) ? rawSource : VENDOR_VIEW_SOURCES.DIRECT;

        const { vendor_category, ...rest } = propsRef.current;
        const { primary, all } = normalizeVendorCategory(vendor_category);

        trackRef.current(events.VENDOR_PROFILE_VIEWED, {
            source,
            vendor_category: primary,
            vendor_categories: all,
            ...rest,
        });
    }, [dedupeKey, events]);
};

export { useVendorProfileViewTracker };
